import { Icon } from '@aqarmap/aqarmap-ui';
import React, { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { addListingDetailsType, ListingDetailsType } from '~addListing/types';
import { MONTHLY_DURATION } from '~addListing/utils/data';

export type RentalDuration = 'DAILY' | 'WEEKLY' | 'MONTHLY';

type Option = {
  key: RentalDuration;
  label: string;
  duration: number;
};

const defaultOptions: Option[] = [
  { key: 'DAILY', label: 'daily', duration: 1 },
  { key: 'WEEKLY', label: 'weekly', duration: 7 },
  { key: 'MONTHLY', label: 'monthly', duration: 30 },
];

const RentalDurationSelector = ({
  listing,
}: {
  listing: ListingDetailsType;
}) => {
  const { t } = useTranslation('app');
  const defaultPaymentPlans = listing?.payment_plans || [
    {
      duration: MONTHLY_DURATION,
      price: listing.price || 0,
    },
  ];

  const { setValue, watch, getValues } =
    useFormContext<addListingDetailsType>();

  const isKeyExist = useCallback(
    (duration: number) => {
      const paymentPlans = getValues('paymentPlans');
      return paymentPlans?.some((item) => item?.duration === duration);
    },
    [watch('paymentPlans')],
  );

  const onChange = (duration: number) => {
    const paymentPlans = getValues('paymentPlans') || [];
    const isExist = isKeyExist(duration);
    if (duration === MONTHLY_DURATION && isExist) return;

    if (isExist) {
      setValue(
        'paymentPlans',
        paymentPlans.filter((item) => item.duration !== duration),
      );
    } else {
      setValue('paymentPlans', [
        ...paymentPlans,
        { duration, price: 0, downPayment: 0 },
      ]);
    }
  };

  useEffect(() => {
    setValue(
      'paymentPlans',
      defaultPaymentPlans.length > 0
        ? defaultPaymentPlans
            .filter((item) => item.duration !== null)
            .map((item) => ({
              price: item.price,
              duration: item.duration,
              downPayment: 0,
            }))
        : [{ duration: 30, price: 0, downPayment: 0 }],
    );
  }, [defaultPaymentPlans]);

  return (
    <div className="flex items-center gap-3">
      {defaultOptions.map((opt) => {
        const isActive = isKeyExist(opt.duration);

        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.duration)}
            className={[
              'flex items-center gap-2 text-body_2',
              'h-[40px] px-3 py-2 rounded-[12px]',
              'border transition-none select-none',
              isActive
                ? 'bg-blue_light_2 border-transparent'
                : 'bg-white border-gray',
            ].join(' ')}
          >
            <span
              className={`w-[16px] h-[16px] rounded-[4px] border border-gray flex justify-center items-center ${isActive ? 'bg-blue' : 'bg-white'}`}
            >
              <Icon name="check" className="text-white" size="12" />
            </span>
            <span
              className={[
                'text-body_2',
                isActive ? 'text-blue' : 'text-gray__dark_1',
              ].join(' ')}
            >
              {t(opt.label)}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default RentalDurationSelector;
